import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "What's a good color palette for a 3BHK living room in Bengaluru?",
  "Italian marble vs. quartz for kitchen tops?",
  "How much does false ceiling cost per sqft?",
  "Tips for vastu-friendly bedroom layout?",
];

// Force external links to open safely; disable raw HTML entirely.
const MD_COMPONENTS = {
  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer nofollow" className="text-[#B38F24] underline underline-offset-2" />,
  ul: (props) => <ul {...props} className="list-disc pl-5 space-y-1 my-2" />,
  ol: (props) => <ol {...props} className="list-decimal pl-5 space-y-1 my-2" />,
  p:  (props) => <p  {...props} className="my-1.5 first:mt-0 last:mb-0" />,
  strong: (props) => <strong {...props} className="font-semibold text-[#1A1A1A]" />,
  code: (props) => <code {...props} className="px-1 py-0.5 rounded bg-[#E8E6DF] text-[#1A1A1A] text-[0.9em]" />,
};

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Aria, your interior assistant. Ask me about materials, layouts, budgets, palettes — anything home." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const isFirstRender = useRef(true);

  // Auto-scroll ONLY the messages container to the latest — never the document.
  // Skip the very first render so the page opens at its natural top.
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data:")) continue;
          try {
            const obj = JSON.parse(ln.slice(5).trim());
            if (obj.session_id && !sessionId) setSessionId(obj.session_id);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry — I had trouble responding. Please try again." };
                return copy;
              });
            }
          } catch { /* skip */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection failed. Please try again." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="assistant-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-20 min-h-[80vh]">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-[#1A1A1A] text-[#D4AF37] flex items-center justify-center">
            <Sparkles size={20} strokeWidth={1.5} />
          </div>
          <div>
            <p className="overline">AI Interior Assistant</p>
            <h1 className="font-heading text-3xl mt-1">Aria — your design co-pilot</h1>
          </div>
        </div>

        <div className="mt-10 card-soft overflow-hidden">
          <div ref={scrollRef} className="h-[55vh] min-h-[420px] overflow-y-auto p-6 md:p-8 space-y-6 no-scrollbar">
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-5 py-4 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1A1A1A] text-white" : "bg-[#F5F3EC] text-[#1A1A1A]"}`}>
                  {m.role === "assistant"
                    ? (m.content
                        ? <ReactMarkdown components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                        : (sending && i === messages.length - 1 ? <span className="text-[#5C5C5C]">Aria is typing…</span> : null))
                    : m.content}
                </div>
              </div>
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="border-t border-[#E8E6DF] p-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)} className="text-xs border border-[#E8E6DF] hover:border-[#1A1A1A] px-3 py-2 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#E8E6DF] p-4 flex items-center gap-3">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Aria anything about your home…"
              className="flex-1 bg-transparent text-sm outline-none px-2"
              disabled={sending}
            />
            <button data-testid="chat-send" disabled={sending || !input.trim()} className="bg-[#D4AF37] text-white hover:bg-[#B38F24] disabled:opacity-40 p-3 transition-colors">
              <Send size={16} />
            </button>
          </form>
        </div>

        <p className="text-xs text-[#5C5C5C] mt-4">Aria uses AI to suggest ideas. Final designs are always validated by our design team.</p>
      </div>
    </div>
  );
}
